import { useState, useRef } from "react";
import { useForm, Controller } from "react-hook-form";
import { useLocation, Link } from "wouter";
import { motion, AnimatePresence } from "framer-motion";
import {
  User, Briefcase, ChevronRight, ChevronLeft, Check,
  Building2, Phone, ShieldCheck, Heart, Award, MapPin
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { useGetLanguages, useGetLocalities, useGetStates, API_BASE } from "@/lib/api-client-react";

/* ─── Constants ─────────────────────────────────────────────────────────── */

const DEFAULT_US_STATES = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA", "HI", "ID", "IL", "IN", "IA",
  "KS", "KY", "LA", "ME", "MD", "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
  "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC", "SD", "TN", "TX", "UT", "VT",
  "VA", "WA", "WV", "WI", "WY", "DC",
];

/* ─── Types ──────────────────────────────────────────────────────────────── */

type FormData = {
  agentType: "individual" | "agency" | "group";
  firstName: string; middleName: string; lastName: string; displayName: string;
  agentNumber: string; licenseNumber: string; experience: string;
  agencyName: string; licensedStates: string[]; languages: string[]; localityIds: string[];
  email: string; phone: string; whatsapp: string;
  buildingNo: string; street1: string; street2: string; city: string; state: string; zipCode: string;
};

/* ─── MultiSelect ────────────────────────────────────────────────────────── */

function MultiSelect({
  options = [], value = [], onChange, placeholder, maxHeight = 260,
}: {
  options: any[]; value: string[]; onChange: (v: string[]) => void;
  placeholder: string; maxHeight?: number;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const normalizedOptions = (options || []).map((o: any) => {
    if (typeof o === "string") return { value: o, label: o };
    if (typeof o === "object" && o !== null) {
      const val = String(o.value ?? o.id ?? o.name ?? o.label ?? "");
      const lbl = String(o.label ?? o.name ?? o.value ?? "");
      return { value: val, label: lbl };
    }
    return { value: String(o ?? ""), label: String(o ?? "") };
  });

  const filtered = normalizedOptions.filter(o =>
    o.label.toLowerCase().includes((search || "").toLowerCase())
  );

  const safeValue = Array.isArray(value) ? value : [];

  const toggle = (optValue: string) => {
    onChange(safeValue.includes(optValue) ? safeValue.filter(v => v !== optValue) : [...safeValue, optValue]);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="w-full min-h-[40px] px-3 py-2 border border-input rounded-md bg-background text-sm text-left flex flex-wrap gap-1.5 items-center hover:border-ring focus:outline-none focus:ring-2 focus:ring-ring/30 transition-colors"
        >
          {safeValue.length === 0 ? (
            <span className="text-muted-foreground">{placeholder}</span>
          ) : (
            safeValue.map(v => {
              const label = normalizedOptions.find(o => o.value === v)?.label || v;
              return (
                <Badge key={v} variant="secondary" className="text-xs rounded-full px-2 py-0.5">
                  {label}
                  <span
                    className="ml-1 cursor-pointer hover:text-destructive"
                    onClick={e => { e.stopPropagation(); toggle(v); }}
                  >
                    &times;
                  </span>
                </Badge>
              );
            })
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] p-0" align="start">
        <div className="p-2 border-b">
          <Input
            placeholder="Search..."
            value={search} onChange={e => setSearch(e.target.value)}
            className="h-8 text-sm"
          />
        </div>
        <div className="p-1 overflow-y-auto" style={{ maxHeight }}>
          {filtered.length === 0 ? (
            <div className="p-2 text-sm text-muted-foreground text-center">No options found.</div>
          ) : (
            filtered.map(opt => {
              const selected = safeValue.includes(opt.value);
              return (
                <div
                  key={opt.value}
                  className="flex items-center gap-2 px-2 py-1.5 text-sm rounded hover:bg-muted cursor-pointer"
                  onClick={() => toggle(opt.value)}
                >
                  <div className={cn(
                    "w-4 h-4 rounded border flex items-center justify-center transition-colors",
                    selected ? "bg-primary border-primary text-white" : "border-input"
                  )}>
                    {selected && <Check className="w-3 h-3" />}
                  </div>
                  <span>{opt.label}</span>
                </div>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

/* ─── Step Indicator ─────────────────────────────────────────────────────── */

const STEPS = [
  { label: "Type", icon: Briefcase },
  { label: "Basic Info", icon: User },
  { label: "Professional", icon: Award },
  { label: "Agency", icon: Briefcase },
  { label: "Contact Info", icon: MapPin },
];

function StepIndicator({ current }: { current: number }) {
  return (
    <div className="flex items-center justify-center gap-0 mb-8">
      {STEPS.map((step, i) => {
        const done = i < current;
        const active = i === current;
        const Icon = step.icon;
        return (
          <div key={i} className="flex items-center">
            <div className="flex flex-col items-center gap-1.5">
              <div className={cn(
                "w-9 h-9 rounded-full flex items-center justify-center border-2 transition-all duration-300",
                done ? "bg-primary border-primary text-white"
                  : active ? "bg-white border-primary text-primary shadow-md shadow-primary/20"
                    : "bg-white border-border text-muted-foreground"
              )}>
                {done ? <Check className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
              </div>
              <span className={cn(
                "text-[11px] font-medium whitespace-nowrap hidden sm:block",
                active ? "text-primary" : done ? "text-foreground" : "text-muted-foreground"
              )}>
                {step.label}
              </span>
            </div>
            {i < STEPS.length - 1 && (
              <div className={cn(
                "w-10 sm:w-16 h-0.5 mb-5 transition-all duration-500",
                i < current ? "bg-primary" : "bg-border"
              )} />
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ─── Field helpers ──────────────────────────────────────────────────────── */

function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return <p className="text-xs text-destructive mt-1">{message}</p>;
}

function FieldLabel({ children, required }: { children: React.ReactNode; required?: boolean }) {
  return (
    <Label className="text-sm font-medium text-foreground">
      {children}{required && <span className="text-destructive ml-0.5">*</span>}
    </Label>
  );
}

/* ─── Main ───────────────────────────────────────────────────────────────── */

export default function AgentRegister() {
  const [, setLocation] = useLocation();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [isDisplayNameManuallyEdited, setIsDisplayNameManuallyEdited] = useState(false);
  const topRef = useRef<HTMLDivElement>(null);

  const { register, control, watch, trigger, setValue, getValues, formState: { errors } } = useForm<FormData>({
    defaultValues: {
      agentType: "individual",
      firstName: "", middleName: "", lastName: "", displayName: "",
      agentNumber: "", licenseNumber: "", experience: "",
      agencyName: "", licensedStates: [], languages: ["English"], localityIds: [],
      email: localStorage.getItem("registerEmail") || "", phone: "", whatsapp: "",
      buildingNo: "", street1: "", street2: "", city: "", state: "", zipCode: ""
    },
    mode: "onChange"
  });

  const { data: statesData = [] } = useGetStates();
  const US_STATES = statesData.length > 0 ? statesData.map((s: any) => typeof s === 'string' ? s : String(s?.code || s?.name || '')) : DEFAULT_US_STATES;
  const { data: languagesData = [] } = useGetLanguages();
  const selectedState = watch("state");
  const selectedLicensedStates = watch("licensedStates");
  const localityStateFilter = selectedState || (selectedLicensedStates && selectedLicensedStates.length > 0 ? selectedLicensedStates : undefined);
  const { data: localitiesData = [] } = useGetLocalities(localityStateFilter);
  const LANGUAGES = languagesData.map((l: any) => l.name);
  const LOCALITIES = localitiesData.map((loc: any) => ({
    value: loc.id.toString(),
    label: `${loc.name} (${loc.state}${loc.city ? ' - ' + loc.city : ''})`
  }));

  const scrollTop = () => topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  const STEP_FIELDS: (keyof FormData)[][] = [
    ["agentType"],
    ["firstName", "middleName", "lastName", "displayName"],
    ["agentNumber", "licenseNumber", "experience"],
    ["agencyName", "state", "localityIds", "languages"],
    ["phone", "whatsapp", "buildingNo", "street1", "street2", "city", "state", "zipCode", "localityIds"]
  ];

  const isStepValid = () => {
    const fields = STEP_FIELDS[step];
    if (!fields || fields.length === 0) return true;

    // Check if any of the fields in this step have errors
    const hasErrors = fields.some(field => errors[field]);
    if (hasErrors) return false;

    // Check required fields based on step
    const values = watch(fields as any);
    if (!values) return false;

    if (step === 0) {
      if (!values[0]) return false; // agentType
    } else if (step === 1) {
      if (!values[0] || !values[2] || !values[3]) return false; // firstName, lastName, displayName
    } else if (step === 2) {
      if (!values[0] || !values[1]) return false; // agentNumber, licenseNumber
    } else if (step === 3) {
      if (!values[0] || !values[1] || !values[2] || values[2].length === 0) return false; // agencyName, state, localityIds
    } else if (step === 4) {
      // phone, street1, city, state, zipCode, localityIds
      if (!values[0] || !values[3] || !values[5] || !values[6] || !values[7] || !values[8] || values[8].length === 0) return false;
    }

    return true;
  };

  const goNext = async () => {
    const valid = await trigger(STEP_FIELDS[step] as any);
    if (!valid) return;
    setStep(s => Math.min(s + 1, STEPS.length - 1));
    scrollTop();
  };

  const goBack = () => { setStep(s => Math.max(s - 1, 0)); scrollTop(); };

  const onSubmit = async () => {
    const valid = await trigger(STEP_FIELDS[step] as any);
    if (!valid) return;
    setLoading(true);
    try {
      const vals = getValues();
      const res = await fetch(`${API_BASE}/auth/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...vals,
          role: "AGENT"
        }),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error);

      localStorage.setItem("agentType", vals.agentType);
      localStorage.removeItem("registerEmail");

      setDone(true);
    } catch (err: any) {
      alert(err.message || "Failed to register");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/5 to-secondary/5 p-6">
        <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="text-center max-w-md">
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center mx-auto mb-6 shadow-lg">
            <Check className="w-10 h-10 text-white" />
          </div>
          <h2 className="text-2xl font-bold mb-2">Registration Submitted!</h2>
          <p className="text-muted-foreground mb-6">
            Your agent profile is under review. Our team will verify your credentials and get back to you shortly.
          </p>
          <Button
            onClick={() => setLocation("/login")}
            className="bg-gradient-to-r from-primary to-secondary text-white rounded-xl h-11 px-8"
          >
            Go to Sign In
          </Button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen grid lg:grid-cols-2" ref={topRef}>
      {/* Sidebar */}
      <div className="hidden lg:flex flex-col bg-gradient-to-br from-primary via-primary/95 to-secondary p-12 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_30%,rgba(255,255,255,0.08),transparent_60%)]" />

        <Link href="/" className="flex items-center gap-3 relative z-10 w-fit">
          <div className="w-10 h-10 rounded-2xl bg-white/20 flex items-center justify-center backdrop-blur-md">
            <Heart className="w-5 h-5 text-white" />
          </div>
          <span className="font-bold text-xl tracking-tight">StraightDoc</span>
        </Link>

        <div className="flex-1 flex flex-col justify-center relative z-10">
          <Badge className="bg-white/20 text-white hover:bg-white/30 border-0 w-fit mb-4">Agent Registration</Badge>
          <motion.h1
            initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}
            className="text-4xl font-bold mb-4 leading-tight"
          >
            Partner with us to support members
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 }}
            className="text-white/70 text-lg mb-8"
          >
            Join our network of dedicated agents providing top-tier support and coordination for our members.
          </motion.p>

          <div className="bg-white/10 rounded-2xl p-5 backdrop-blur border border-white/20">
            <p className="text-sm font-medium mb-4">Why join as an Agent?</p>
            {[
              "Manage client portfolios easily",
              "Access dedicated agent tools",
              "Streamlined communication",
              "Real-time coordination dashboards",
            ].map((item, i) => (
              <motion.div
                key={item}
                initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 + i * 0.1 }}
                className="flex items-center gap-3 text-white/90 mb-3 last:mb-0"
              >
                <div className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                  <ShieldCheck className="w-3 h-3 text-white" />
                </div>
                <span className="text-sm">{item}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Main Form Area */}
      <div className="flex flex-col p-6 sm:p-10 bg-background relative overflow-y-auto">
        <div className="w-full max-w-xl mx-auto relative z-10 my-auto pt-4 pb-12">

          {/* Mobile Header */}
          <div className="flex items-center justify-between mb-8 lg:hidden">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                <Heart className="w-4 h-4 text-white" />
              </div>
              <span className="font-bold text-lg">StraightDoc</span>
            </div>
            <Link href="/login" className="text-sm text-primary font-medium hover:underline">Sign in</Link>
          </div>

          {/* Title */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary text-sm font-medium px-3 py-1.5 rounded-full mb-3">
              <Briefcase className="w-4 h-4" />
              Agent Registration
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold mb-1">Become a Certified Agent</h1>
            <p className="text-muted-foreground">Complete your agent profile in {STEPS.length} simple steps</p>
          </div>

          {/* Step Indicator */}
          <StepIndicator current={step} />

          {/* Form Card */}
          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              initial={{ opacity: 0, x: 24 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -24 }}
              transition={{ duration: 0.22 }}
              className="bg-white rounded-2xl border border-border shadow-sm p-6 sm:p-8"
            >
              {/* ── Step 0: Registration Type ── */}
              {step === 0 && (
                <div className="space-y-5">
                  <div className="mb-4">
                    <SectionHeader icon={Briefcase} title="Registration Type" subtitle="How are you registering with StraightDoc?" />
                    <div className="grid grid-cols-2 gap-4 mt-4">
                      <button
                        type="button"
                        onClick={() => setValue("agentType", "individual")}
                        className={`p-4 border rounded-xl flex items-start gap-4 transition-all text-left ${watch("agentType") === "individual" ? "border-primary bg-primary/5 ring-1 ring-primary" : "hover:border-primary/50"}`}
                      >
                        <User className={`w-5 h-5 mt-0.5 ${watch("agentType") === "individual" ? "text-primary" : "text-muted-foreground"}`} />
                        <div>
                          <div className="font-semibold">Individual</div>
                          <div className="text-sm text-muted-foreground mt-1">I practice independently and manage my own profile.</div>
                        </div>
                      </button>
                      <button
                        type="button"
                        onClick={() => setValue("agentType", "group")}
                        className={`p-4 border rounded-xl flex items-start gap-4 transition-all text-left ${watch("agentType") === "group" ? "border-primary bg-primary/5 ring-1 ring-primary" : "hover:border-primary/50"}`}
                      >
                        <Building2 className={`w-5 h-5 mt-0.5 ${watch("agentType") === "group" ? "text-primary" : "text-muted-foreground"}`} />
                        <div>
                          <div className="font-semibold">Agency</div>
                          <div className="text-sm text-muted-foreground mt-1">You can add and manage multiple agents from your dashboard.</div>
                        </div>
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* ── Step 1: Basic Info ── */}
              {step === 1 && (
                <div className="space-y-5">
                  <SectionHeader icon={User} title="Basic Information" subtitle="Your name and personal details" />

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <FieldLabel required>First Name</FieldLabel>
                      <Input
                        {...register("firstName", {
                          required: "First name is required",
                          onChange: (e) => {
                            if (!isDisplayNameManuallyEdited) {
                              const vals = getValues();
                              const newFirst = e.target.value;
                              setValue("displayName", `${newFirst} ${vals.lastName}`.trim(), { shouldValidate: true });
                            }
                          }
                        })}
                        placeholder="John" className="mt-1.5"
                      />
                      <FieldError message={errors.firstName?.message} />
                    </div>
                    <div>
                      <FieldLabel>Middle Name</FieldLabel>
                      <Input {...register("middleName")} placeholder="A." className="mt-1.5" />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <FieldLabel required>Last Name</FieldLabel>
                      <Input
                        {...register("lastName", {
                          required: "Last name is required",
                          onChange: (e) => {
                            if (!isDisplayNameManuallyEdited) {
                              const vals = getValues();
                              const newLast = e.target.value;
                              setValue("displayName", `${vals.firstName} ${newLast}`.trim(), { shouldValidate: true });
                            }
                          }
                        })}
                        placeholder="Smith" className="mt-1.5"
                      />
                      <FieldError message={errors.lastName?.message} />
                    </div>
                    <div>
                      <FieldLabel required>Display Name</FieldLabel>
                      <Input
                        {...register("displayName", {
                          required: "Display name is required",
                          onChange: () => setIsDisplayNameManuallyEdited(true)
                        })}
                        placeholder="John Smith" className="mt-1.5"
                      />
                      <FieldError message={errors.displayName?.message} />
                    </div>
                  </div>
                </div>
              )}

              {/* ── Step 2: Professional Details ── */}
              {step === 2 && (
                <div className="space-y-5">
                  <SectionHeader icon={Award} title="Professional Details" subtitle="Your agent credentials and licenses" />

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <FieldLabel required>Agent Number</FieldLabel>
                      <Input
                        {...register("agentNumber", { required: "Agent Number is required" })}
                        placeholder="AGT-123456" className="mt-1.5"
                      />
                      <FieldError message={errors.agentNumber?.message} />
                    </div>
                    <div>
                      <FieldLabel required>State License Number</FieldLabel>
                      <Input
                        {...register("licenseNumber", { required: "License number is required" })}
                        placeholder="AB-12345" className="mt-1.5"
                      />
                      <FieldError message={errors.licenseNumber?.message} />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <FieldLabel>Years of Experience</FieldLabel>
                      <Controller
                        name="experience" control={control}
                        render={({ field }) => (
                          <Select value={field.value} onValueChange={field.onChange}>
                            <SelectTrigger className="mt-1.5"><SelectValue placeholder="Select years" /></SelectTrigger>
                            <SelectContent>
                              {["Less than 1 year", "1-3 years", "3-5 years", "5-10 years", "10+ years"].map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        )}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* ── Step 3: Agency & Coverage ── */}
              {step === 3 && (
                <div className="space-y-5">
                  <SectionHeader icon={Briefcase} title="Agency & Coverage" subtitle="Where you work and languages you speak" />

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="sm:col-span-2">
                      <FieldLabel required>Agency / Company Name</FieldLabel>
                      <Input
                        {...register("agencyName", { required: "Agency name is required" })}
                        placeholder="Global Health Agents LLC" className="mt-1.5"
                      />
                      <FieldError message={errors.agencyName?.message} />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <FieldLabel required>State</FieldLabel>
                      <div className="mt-1.5">
                        <Controller
                          name="state" control={control} rules={{ required: "State is required" }}
                          render={({ field }) => (
                            <Select value={field.value} onValueChange={(val) => {
                              field.onChange(val);
                              setValue("licensedStates", [val]);
                              setValue("localityIds", []);
                            }}>
                              <SelectTrigger className="rounded-xl h-11"><SelectValue placeholder="Select state" /></SelectTrigger>
                              <SelectContent className="max-h-60 rounded-xl">
                                {US_STATES.map((s: string, idx: number) => (
                                  <SelectItem key={`${s}-${idx}`} value={s}>{s}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        />
                      </div>
                      {errors.state && <p className="text-xs text-destructive mt-1">Select a state</p>}
                    </div>
                    <div>
                      <FieldLabel required>Locality</FieldLabel>
                      <div className="mt-1.5">
                        <Controller
                          name="localityIds" control={control} rules={{ required: "Locality is required" }}
                          render={({ field }) => (
                            <MultiSelect
                              options={LOCALITIES}
                              value={field.value}
                              onChange={field.onChange}
                              placeholder={selectedState ? "Select locality" : "Select state first"}
                            />
                          )}
                        />
                      </div>
                      {errors.localityIds && <p className="text-xs text-destructive mt-1">Select at least one locality</p>}
                    </div>

                    <div className="sm:col-span-2">
                      <FieldLabel>Languages Spoken</FieldLabel>
                      <div className="mt-1.5">
                        <Controller
                          name="languages" control={control}
                          render={({ field }) => (
                            <MultiSelect options={LANGUAGES} value={field.value} onChange={field.onChange} placeholder="Select languages" />
                          )}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* ── Step 4: Contact & Address ── */}
              {step === 4 && (
                <div className="space-y-5">
                  <SectionHeader icon={MapPin} title="Contact Information" subtitle="How we can reach you" />

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <FieldLabel required>Phone Number</FieldLabel>
                      <Input
                        {...register("phone", {
                          required: "Phone number is required",
                          pattern: { value: /^[^a-zA-Z]+$/, message: "Alphabets are not allowed" }
                        })}
                        onInput={(e) => { e.currentTarget.value = e.currentTarget.value.replace(/[a-zA-Z]/g, '') }}
                        type="tel" placeholder="(555) 123-4567" className="mt-1.5"
                      />
                      <FieldError message={errors.phone?.message} />
                    </div>
                    <div>
                      <FieldLabel>WhatsApp Number</FieldLabel>
                      <Input
                        {...register("whatsapp", {
                          pattern: { value: /^[^a-zA-Z]*$/, message: "Alphabets are not allowed" }
                        })}
                        onInput={(e) => { e.currentTarget.value = e.currentTarget.value.replace(/[a-zA-Z]/g, '') }}
                        type="tel" placeholder="(555) 123-4567" className="mt-1.5"
                      />
                    </div>
                  </div>

                  <div className="space-y-4 pt-2">
                    <h3 className="font-medium flex items-center gap-2 text-foreground text-sm"><Building2 className="w-4 h-4" /> Mailing Address</h3>
                    <div className="grid sm:grid-cols-3 gap-4">
                      <div>
                        <FieldLabel>Building No.</FieldLabel>
                        <Input {...register("buildingNo")} placeholder="123A" className="mt-1.5" />
                      </div>
                      <div className="sm:col-span-2">
                        <FieldLabel required>Street Name 1</FieldLabel>
                        <Input
                          {...register("street1", { required: "Street name is required" })}
                          placeholder="Main St" className="mt-1.5"
                        />
                        <FieldError message={errors.street1?.message} />
                      </div>
                    </div>

                    <div>
                      <FieldLabel>Street Name 2</FieldLabel>
                      <Input {...register("street2")} placeholder="Apt 4B" className="mt-1.5" />
                    </div>

                    <div className="grid sm:grid-cols-3 gap-4">
                      <div>
                        <FieldLabel required>City</FieldLabel>
                        <Input
                          {...register("city", { required: "City is required" })}
                          placeholder="New York" className="mt-1.5"
                        />
                        <FieldError message={errors.city?.message} />
                      </div>
                      <div>
                        <FieldLabel required>State</FieldLabel>
                        <Controller
                          name="state" control={control} rules={{ required: "State is required" }}
                          render={({ field }) => (
                            <Select value={field.value} onValueChange={field.onChange}>
                              <SelectTrigger className="mt-1.5"><SelectValue placeholder="State" /></SelectTrigger>
                              <SelectContent className="max-h-60">
                                {US_STATES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          )}
                        />
                        <FieldError message={errors.state?.message} />
                      </div>
                      <div>
                        <FieldLabel required>Zip Code</FieldLabel>
                        <Input
                          {...register("zipCode", { required: "Zip Code is required" })}
                          placeholder="10001" className="mt-1.5"
                        />
                        <FieldError message={errors.zipCode?.message} />
                      </div>
                    </div>

                    <div>
                      <FieldLabel required>Primary Locality / Area</FieldLabel>
                      <p className="text-xs text-muted-foreground mt-0.5 mb-1.5">
                        {selectedState ? `Showing localities for ${selectedState}` : "Select state above to filter localities"}
                      </p>
                      <div className="mt-1.5">
                        <Controller
                          name="localityIds" control={control} rules={{ required: "At least one locality is required" }}
                          render={({ field }) => (
                            <MultiSelect options={LOCALITIES} value={field.value} onChange={field.onChange} placeholder={selectedState ? `Select localities in ${selectedState}...` : "Select localities..."} />
                          )}
                        />
                      </div>
                      {errors.localityIds && <p className="text-xs text-destructive mt-1">Select at least one locality</p>}
                    </div>
                  </div>
                </div>
              )}
            </motion.div>
          </AnimatePresence>

          {/* Navigation */}
          <div className="flex items-center justify-between mt-6">
            <div className="text-xs text-muted-foreground">Step {step + 1} of {STEPS.length}</div>

            <div className="flex items-center gap-3">
              <Button
                type="button" variant="outline"
                className="rounded-xl h-11 px-6"
                onClick={step === 0 ? () => setLocation("/login") : goBack}
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                {step === 0 ? "Back to Login" : "Back"}
              </Button>

              {step < STEPS.length - 1 ? (
                <Button
                  type="button"
                  className="rounded-xl h-11 px-6 bg-gradient-to-r from-primary to-secondary text-white"
                  onClick={goNext}
                  disabled={!isStepValid()}
                >
                  Continue <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              ) : (
                <Button
                  type="button"
                  className="rounded-xl h-11 px-6 bg-gradient-to-r from-primary to-secondary text-white"
                  onClick={onSubmit}
                  disabled={loading || !isStepValid()}
                >
                  {loading ? "Submitting…" : <><Check className="w-4 h-4 mr-1.5" />Submit Registration</>}
                </Button>
              )}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

/* ─── SectionHeader ──────────────────────────────────────────────────────── */

function SectionHeader({ icon: Icon, title, subtitle }: { icon: React.ElementType; title: string; subtitle: string }) {
  return (
    <div className="flex items-start gap-3 pb-4 border-b border-border">
      <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
        <Icon className="w-5 h-5 text-primary" />
      </div>
      <div>
        <h2 className="font-semibold text-lg">{title}</h2>
        <p className="text-sm text-muted-foreground">{subtitle}</p>
      </div>
    </div>
  );
}
